<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('templateTable.title') }}
    </v-toolbar>
    <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        class="elevation-12"
        hide-default-footer
    >

      <template v-slot:top>
        <v-sheet color="grey lighten-3"
                 class="px-4 pt-4 pb-3"
                 tile>
          <h3 class="transition-swing font-weight-medium">{{ $t('templateTable.filterTitle') }}</h3>
          <v-responsive>
            <v-row>
              <v-col cols="12" sm="6" md="3" class="py-2">
                <v-autocomplete v-model="organizationId"
                                :items="organizations"
                                :label="$t('addTemplateForm.labels.organization')"
                                :dense="true"
                                :clearable="true"
                                item-text="name"
                                item-value="id"
                                @change="onFiltersChanged"
                />
              </v-col>
              <v-spacer/>
              <v-col cols="12" sm="6" md="3" class="py-2 text-right">
                <v-btn color="primary" @click="onAddTemplateButtonClicked">{{ $t('templateTable.addBtn') }}</v-btn>
                <v-dialog v-model="addTemplateDialog" :persistent="true" width="600px">
                  <AddTemplateComponent
                      @cancel="onAddTemplateCanceled"
                      @added="onAddTemplateSuccess"
                      ref="addAdminTemplateComponent"/>
                </v-dialog>
              </v-col>
            </v-row>
          </v-responsive>
        </v-sheet>
      </template>

      <template v-slot:item.name="{item}">
        <template>
          {{ item.name }}
        </template>
      </template>

      <template v-slot:item.actions="{item}">
        <v-btn icon @click="onDownloadTemplateButtonClicked(item)">
          <v-icon>mdi-download</v-icon>
        </v-btn>
        <v-btn icon @click="onEditTemplateButtonClicked(item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="editTemplateDialog" :persistent="true" width="600px">
      <EditTemplateComponent ref="editTemplate" @cancel="onEditTemplateCanceled" @updated="onEditTemplateSuccess" />
    </v-dialog>

    <v-snackbar v-model="isAddTemplateSuccess"
                :timeout="5000"
                :top="true"
                color="success"
    >
      {{ $t('templateTable.messages.addSuccess') }}
    </v-snackbar>

    <v-snackbar v-model="isEditTemplateSuccess"
                :timeout="5000"
                :top="true"
                color="success"
    >
      {{ $t('templateTable.messages.editSuccess') }}
    </v-snackbar>

  </v-card>
</template>


<script>
import _ from "underscore";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import OrganizationService from "@/services/organization.service";
import Mappers from "@/mappers";
import AddTemplateComponent from "@/components/template/AddTemplateComponent.vue";
import EditTemplateComponent from "@/components/template/EditTemplateComponent.vue";
import TemplateService from "@/services/template.service";
import AdminTemplateService from "@/services/admin.template.service";

export default {
  name: "TemplateDataTable",
  components: {AddTemplateComponent, EditTemplateComponent},
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 50,
      },
      organizations: [],
      templates: [],
      organizationId: null,

      addTemplateDialog: false,
      editTemplateDialog: false,
      isAddTemplateSuccess: false,
      isEditTemplateSuccess: false
    }
  },
  computed: {
    headers() {
      const headers = []

      headers.push({
        text: this.$t('templateTable.headers.number'),
        align: 'center',
        sortable: false,
        value: 'displayNumber'
      })

      headers.push({
        text: this.$t('templateTable.headers.name'),
        align: 'left',
        sortable: true,
        value: 'Name'
      })

      headers.push({
        text: this.$t('templateTable.headers.organization'),
        align: 'left',
        sortable: true,
        value: 'Organization.name'
      })

      headers.push({
        text: '',
        align: 'right',
        sortable: false,
        value: 'actions'
      })

      return headers
    },
    items() {
      let number = 1;
      let skip = this.skip;
      return _.map(this.templates, u => {
        u.displayNumber = skip + (number++)
        return u
      })

    },
    skip() {
      return (this.options.page - 1) * this.options.itemsPerPage
    }
  },
  methods: {
    updateItems() {
      this.$emit("update", this.options, this.filters)
    },
    onEditTemplateButtonClicked(template) {
      this.editTemplateDialog = true;
      this.$nextTick(() => {
        this.$refs.editTemplate.set(template);
      });
    },
    onDownloadTemplateButtonClicked(template){
      this.$store.dispatch(LAYOUT_LOADING_START)

      var id = template.Id
      AdminTemplateService.download({id: id})
          .then(response => {
            const link = document.createElement('a');
            document.body.appendChild(link);

            const blobFile = new Blob([response.data], { type: 'text/html' });
            const blobUrl = window.URL.createObjectURL(blobFile);

            link.href = blobUrl;
            link.download = template.FileName;
            link.click();

            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          })
          .catch(error => console.log(error))
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    onFiltersChanged() {
      this.updateItems();
      this.loadTemplates()
    },
    onAddTemplateButtonClicked() {
      this.addTemplateDialog = true
      this.$nextTick(() => {
        this.$refs.addAdminTemplateComponent.init()
      })
    },
    onAddTemplateCanceled() {
      this.addTemplateDialog = false
    },
    onAddTemplateSuccess() {
      this.isAddTemplateSuccess = true
      this.addTemplateDialog = false
      this.onFiltersChanged()
    },
    onEditTemplateCanceled() {
      this.editTemplateDialog = false
    },
    onEditTemplateSuccess() {
      this.isEditTemplateSuccess = true
      this.editTemplateDialog = false
      this.onFiltersChanged()
    },
    loadOrganizations() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      OrganizationService.getOrganizations({})
          .then(response => {
            this.organizations = _.map(response.data.Records, o => {
              return Mappers.mapOrganization(o?.Organization)
            })
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    loadTemplates(){
      this.loading = true
      this.$store.dispatch(LAYOUT_LOADING_START)

      let id = this.organizationId;

      TemplateService.getTemplate({id})
          .then(response => {
            if(response.data.length === 0){
              this.templates = []
            }
            else{
              this.templates = _.map(response.data, o => {
                return Mappers.mapTemplate(o)
              })
            }
          })
          .finally(() => {
            this.loading = false
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    }
  },
  created() {
    this.loadOrganizations()
    this.loadTemplates()

    if (this.$route.query.inn)
      this.filters.inn = this.$route.query.inn
  },
  watch: {
    options: {
      handler() {
        this.updateItems()
      }
    }
  }
}
</script>

<style scoped>

</style>